import React, { useEffect, useRef, useState } from "react";
import Chart from "react-apexcharts";
import type { ApexOptions } from "apexcharts";
import "./chronicBreakdownChart.scss";
import { useChronicBreakdownStore } from "@stores/chronicBreakdownStore";
import { Col, Row, Spin } from "antd";
import HealthemSpinner from "@components/healthemSpinner/healthemSpinner";
import reportService from "@services/report.service";
import { useNavigate } from "react-router-dom";
import { routeMap } from "@utils/constants";
import { isEmpty } from "lodash";
import DataNotFound from "@components/noDataFound";
import { NO_DATA_AVAILABLE } from "../../../../constants/constants";

interface TooltipData {
  name: string;
  splitName?: string;
  totalPatientCount: number;
  percentage: number;
  percentageLabel: any;
}

const BarChart: React.FC = () => {
  const chartContainerRef = useRef<HTMLDivElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const chartData = useChronicBreakdownStore((state) => state.chartData);
  const chartLoading = useChronicBreakdownStore((state) => state.chartLoading);
  const [tooltipData, setTooltipData] = useState<TooltipData | null>(null);
  const [tooltipVisible, setTooltipVisible] = useState<boolean>(false);
  const [position, setPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const [showLoader, setShowLoader] = useState<boolean>(false);
  const prevAppliedFilters = useChronicBreakdownStore((state) => state.appliedProvider);
  const patientIdsHandler = useChronicBreakdownStore((state) => state.patientIdsHandler);
  const memberPageNavigationHandler = useChronicBreakdownStore((state) => state.memberPageNavigationHandler);
  const setChronicName = useChronicBreakdownStore((state) => state.setChronicName);

  const navigate = useNavigate();
  const barData = Array.isArray(chartData?.conditions) ? chartData.conditions : [];
  const sortedConditions = [...barData].sort((a, b) => b.percentage - a.percentage);
  const maxPercentage = sortedConditions.length ? sortedConditions[0].percentage : 0;
  const [year, state, city, healthPlan, pcpVendor, provider] = prevAppliedFilters;
  const isSingleYear = Array.isArray(year) && year.length === 1;

  const hideTooltipWithDelay = () => {
    hideTimeoutRef.current = setTimeout(() => {
      const isBarHovered = chartContainerRef.current?.querySelector(".apexcharts-bar-area:hover");
      const isTooltipHovered = tooltipRef.current?.matches(":hover");

      if (!isBarHovered && !isTooltipHovered) {
        setTooltipVisible(false);
      }
    }, 0);
  };

  const cancelHideTooltip = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  };

  const handleTooltipClick = (name: any, splitName?: any) => {
    setShowLoader(true);
    const chronicSplitName = splitName ? splitName : undefined

    reportService.getChronicBreakdownReportPatientIds(year, state, city, healthPlan, pcpVendor, provider, name, chronicSplitName).subscribe({
      next: (response) => {
        patientIdsHandler(response?.data?.data);
        setChronicName(name);
        memberPageNavigationHandler(true);
        setShowLoader(true);
        navigate(routeMap["members"]);
        setShowLoader(false);
      },
      error: (error) => {
        console.error(error);
        setShowLoader(false);
      },
    });
  };

  useEffect(() => {
    const handleGlobalMouseMove = (event: MouseEvent) => {
      const tooltipEl = tooltipRef.current;
      const chartEl = chartContainerRef.current;

      const isInsideTooltip = tooltipEl?.contains(event.target as Node);
      const isInsideChart = chartEl?.querySelector(".apexcharts-bar-area:hover");

      if (!isInsideTooltip && !isInsideChart) {
        setTooltipVisible(false);
      }
    };

    const handleScroll = () => {
      setTooltipVisible(false);
    };

    document.addEventListener("mousemove", handleGlobalMouseMove);
    window.addEventListener("scroll", handleScroll, true);

    return () => {
      document.removeEventListener("mousemove", handleGlobalMouseMove);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, []);

  const buildSeries = (conditions: any[], isSingleYear: boolean) => {
    const MIN_SEGMENT = 3;
    if (isSingleYear) {
      const hasSplit = conditions.some((c) => c.recapturedPercent > 0 && c.pendingPercent > 0);
      if (hasSplit) {
        return [
          {
            name: "Recaptured",
            data: conditions.map((c) => {
              const adjusted = c.percentage < 1 ? 8.5 : c.percentage + 7.5;
              let recapturedY = (adjusted * c.recapturedPercent) / 100;
              let pendingY = (adjusted * c.pendingPercent) / 100;

              if (pendingY > 0 && pendingY < MIN_SEGMENT) {
                pendingY = MIN_SEGMENT;
                recapturedY = adjusted - pendingY;
              }
              return { x: c.name, y: recapturedY, count: c.recapturedCount, ...c };
            }),
          },
          {
            name: "Pending",
            data: conditions.map((c) => {
              const adjusted = c.percentage < 1 ? 8.5 : c.percentage + 6;
              let recapturedY = (adjusted * c.recapturedPercent) / 100;
              let pendingY = (adjusted * c.pendingPercent) / 100;

              if (pendingY > 0 && pendingY < MIN_SEGMENT) {
                pendingY = MIN_SEGMENT;
                recapturedY = adjusted - pendingY;
              }
              return { x: c.name, y: pendingY, count: c.pendingCount, ...c };
            }),
          },
        ];
      }
    }

    return [
      {
        name: "Percentage",
        data: conditions.map((c) => {
          const adjusted = c.percentage < 1 ? 8.5 : c.percentage + 8;
          return { x: c.name, y: adjusted, actualPercentage: c.percentage, label: c.percentageLabel, totalPatientCount: c.totalPatientCount };
        }),
      },
    ];
  };

  const getTooltipInfo = (condition: any, seriesIndex: number, isSingleYear: boolean): TooltipData | null => {
    const isRecaputredAndPendingZero = condition.recapturedCount === 0 && condition.pendingCount === 0;

    if (isSingleYear && !isRecaputredAndPendingZero) {
      if (seriesIndex === 0) {
        return {
          name: condition.name,
          splitName: "Recaptured",
          totalPatientCount: condition.recapturedCount,
          percentage: condition.recapturedPercent,
          percentageLabel: `${condition.recapturedPercent}%`,
        };
      }
      if (seriesIndex === 1) {
        return {
          name: condition.name,
          splitName: "Pending",
          totalPatientCount: condition.pendingCount,
          percentage: condition.pendingPercent,
          percentageLabel: `${condition.pendingPercent}%`,
        };
      }
    }

    return {
      name: condition.name,
      totalPatientCount: condition.totalPatientCount,
      percentage: condition.percentage,
      percentageLabel: condition.percentageLabel,
    };
  };

  const options: ApexOptions = {
    chart: {
      type: "bar",
      toolbar: { show: false },
      stacked: true,
      events: {
        dataPointMouseEnter: (event: MouseEvent, chartContext: any, config: any) => {
          cancelHideTooltip();

          const { seriesIndex, dataPointIndex } = config;
          const condition = sortedConditions[dataPointIndex];
          const barElement = (event.target as HTMLElement).closest(".apexcharts-bar-area") as HTMLElement;
          const tooltipInfo = getTooltipInfo(condition, seriesIndex, isSingleYear);

          if (chartContainerRef.current && barElement && tooltipInfo) {
            const containerEl = chartContainerRef.current;
            const chartEl = containerEl.closest(".chronic-chart") as HTMLElement;
            const chartRect = chartEl.getBoundingClientRect();
            const barRect = barElement.getBoundingClientRect();

            const relativeX = barRect.left + barRect.width / 2 - chartRect.left;
            const relativeY = barRect.top - chartRect.top;

            setPosition({ left: relativeX, top: relativeY });
            setTooltipData(tooltipInfo);
            setTooltipVisible(true);
          }
        },
        dataPointMouseLeave: hideTooltipWithDelay,
      },
      animations: { enabled: false },
    },
    colors: isSingleYear ? ["#2F6BBB", "#7CE0C2"] : ["#2F6BBB"],
    plotOptions: {
      bar: {
        horizontal: true,
        barHeight: "20",
        borderRadius: 2,
        dataLabels: {
          position: "center",
          total: {
            enabled: true,
            offsetX: 8,
            formatter: function (_total, opts) {
              return sortedConditions[opts.dataPointIndex]?.percentageLabel || "";
            },
            style: {
              fontSize: "13px",
              fontWeight: 600
            },
          },
        },
      },
    },
    dataLabels: {
      enabled: false,
    },
    yaxis: {
      labels: {
        minWidth: 300,
        maxWidth: 300,
        offsetX: -5,
      },
    },
    xaxis: {
      categories: sortedConditions.map((condition) => condition.name),
      title: {
        text: "",
      },
      labels: { show: false },
      axisBorder: { show: false },
      axisTicks: { show: false },
      min: 0,
      max: Math.ceil(maxPercentage * 1.5),
    },
    tooltip: {
      enabled: false,
    },
    grid: {
      show: false,
      padding: {
        left: 20,
      },
    },
    states: {
      hover: {
        filter: {
          type: "none",
        },
      },
      active: {
        filter: {
          type: "none",
        },
      },
    },
    legend: {
      show: false,
    },
  };

  const series = buildSeries(sortedConditions, isSingleYear);

  return (
    <Spin spinning={chartLoading} indicator={<HealthemSpinner />}>
      <div className="chronic-chart">
        <Row className="chronic-chart__header mt-1 mr-1 ml-1 mb-1 f500" justify="space-between" align="top">
          <Col>
            <span>Members By Chronic Conditions</span>
          </Col>
          <Col>
            <div style={{ textAlign: "right" }}>
              <div className="italic">Total Members: {chartData?.totalCount ? chartData.totalCount.toLocaleString() : 0}</div>

              {year > 1 && (
                <div className="mt-1">
                  <span className="chronic-chart__header__legend-dot recaptured" />
                  <span className="mr-1">Recaptured</span>
                  <span className="chronic-chart__header__legend-dot pending" />
                  Pending
                </div>
              )}
            </div>
          </Col>
        </Row>
        {isEmpty(chartData?.conditions) ? (
          <div className="chronic-chart__body">
            <DataNotFound message={NO_DATA_AVAILABLE} />
          </div>
        ) : (
          <>
            <div className="chronic-chart__body" ref={chartContainerRef}>
              <Chart options={options} series={series} type="bar" height={Math.max(barData.length * 35, 200)} width={750} />
            </div>
            {tooltipVisible && tooltipData && (
              <div ref={tooltipRef} className="custom-tooltip-with-arrow" style={{ left: position.left, top: position.top }} onMouseEnter={cancelHideTooltip} onMouseLeave={hideTooltipWithDelay}>
                <Spin spinning={showLoader} indicator={<HealthemSpinner />}>
                  <div className="tooltip-header f500">
                    <strong className="mr-05">{tooltipData.percentageLabel}</strong>
                    <span onClick={() => handleTooltipClick(tooltipData.name, tooltipData.splitName)}>
                      {tooltipData.totalPatientCount.toLocaleString()}
                      <i className="icon icon-external-link ml-02" />
                    </span>
                  </div>
                  <div className="tooltip-name">{tooltipData.splitName
                    ? `${tooltipData.splitName} - ${tooltipData.name}`
                    : tooltipData.name}</div>
                </Spin>
              </div>
            )}
          </>
        )}
      </div>
    </Spin>
  );
};

export default BarChart;